import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle, Trash2, Save, Plus, ChevronDown } from 'lucide-react';
import { api, type System, type Mapping, type UnmappedWritingId, type Agent } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../contexts/ToastContext';
import { isHouseForSystem } from '../lib/format';

/**
 * Standalone page wrapper — kept for the legacy `/:sys/mappings/:carrier`
 * route. Reads sys + carrier from the URL and renders the embeddable body
 * with the back-link chrome.
 */
export default function MappingsCarrier() {
  const { sys: sysSlug, carrier } = useParams<{ sys: string; carrier: string }>();
  const sys = sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null;
  const carrierName = carrier ? decodeURIComponent(carrier) : null;
  if (!sys || !carrierName) return <div className="alert alert-warning"><p>Bad URL</p></div>;
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3">
        <Link
          to={`/${sysSlug}/carriers`}
          className="btn-icon"
          style={{ color: 'var(--color-text-muted)' }}
          aria-label="Back to carriers"
        >
          <ArrowLeft size={20} />
        </Link>
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
          Mappings — {sys} / {carrierName}
        </h2>
      </div>
      <CarrierMappingsBody sys={sys} carrier={carrierName} />
    </div>
  );
}

/**
 * Embeddable body — renders the unmapped section + mappings table for a
 * given (sys, carrier). Used inside the tabbed Carriers page.
 */
export function CarrierMappingsBody({ sys, carrier: carrierName }: { sys: System; carrier: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['mappings', sys, carrierName],
    queryFn: () => api.listMappings(sys as System, carrierName as string),
    enabled: !!sys && !!carrierName,
  });

  const agentsQ = useQuery({
    queryKey: ['agents-all'],
    queryFn: () => api.listAgents(),
  });
  const existingAgents = (agentsQ.data?.agents ?? []).filter(
    (a) => a.agent_id !== 'unpaid' && a.agent_id !== 'unknown',
  );

  if (q.isLoading) return <LoadingSpinner label="Loading mappings…" />;
  if (q.isError) return <div className="alert alert-error"><p>{(q.error as Error).message}</p></div>;

  const data = q.data!;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['mappings', sys, carrierName] });
    queryClient.invalidateQueries({ queryKey: ['mappings', sys] });
    queryClient.invalidateQueries({ queryKey: ['carrier-rows', sys, carrierName] });
    queryClient.invalidateQueries({ queryKey: ['batch'] });
    queryClient.invalidateQueries({ queryKey: ['batch-rows'] });
  };

  const handleSave = async (
    writingId: string,
    agentName: string,
    opts?: { direct_pay_by_carrier?: boolean; non_paid?: boolean },
  ) => {
    if (!agentName.trim()) {
      toast.error('Agent name is required');
      throw new Error('agent name required');
    }
    // Suppress the success toast when only flipping flags — the optimistic
    // checkbox is feedback enough, no need for a popup on every click.
    const isFlagOnly =
      opts !== undefined &&
      (opts.direct_pay_by_carrier !== undefined || opts.non_paid !== undefined);
    try {
      const r = await api.upsertMapping(sys, carrierName, writingId, agentName, opts);
      if (!isFlagOnly) {
        toast.success(
          `Mapped ${writingId} → ${agentName}. Re-attributed ${r.reattributed} rows.`,
        );
      }
      refresh();
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
      // Re-throw so the optimistic UI in the row knows to roll back
      throw err;
    }
  };

  const handleDelete = async (writingId: string) => {
    if (!confirm(`Delete mapping for ${writingId}?`)) return;
    try {
      await api.deleteMapping(sys, carrierName, writingId);
      toast.info(`Deleted mapping for ${writingId}`);
      refresh();
    } catch (err) {
      toast.error(`Delete failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
        <span className="font-mono">{data.totals.mapped_rows}</span> of{' '}
        <span className="font-mono">{data.totals.total_rows}</span> rows attributed ·{' '}
        <span className="font-mono">{data.totals.mapped_writing_ids}</span> mapped writing IDs
        {data.totals.unmapped_writing_ids > 0 && (
          <> · <span className="text-amber-500 font-mono">{data.totals.unmapped_writing_ids}</span> unmapped</>
        )}
      </p>

      {data.unmapped.length > 0 && (
        <UnmappedSection
          unmapped={data.unmapped}
          existingAgents={existingAgents}
          sys={sys}
          onSave={handleSave}
        />
      )}

      <ExistingMappingsSection
        mappings={data.mappings}
        existingAgents={existingAgents}
        sys={sys}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  );
}

function UnmappedSection({
  unmapped,
  existingAgents,
  sys,
  onSave,
}: {
  unmapped: UnmappedWritingId[];
  existingAgents: Agent[];
  sys: System;
  onSave: (
    writingId: string,
    agentName: string,
    opts?: { direct_pay_by_carrier?: boolean; non_paid?: boolean },
  ) => Promise<void> | void;
}) {
  return (
    <div className="card p-0 overflow-hidden">
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'rgba(245, 158, 11, 0.06)' }}
      >
        <AlertTriangle size={16} className="text-amber-500" />
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          Unmapped Writing IDs ({unmapped.length})
        </h3>
        <p className="text-xs ml-auto" style={{ color: 'var(--color-text-muted)' }}>
          Sorted by row count
        </p>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Writing ID</th>
            <th className="text-right">Rows</th>
            <th>Sample policyholders</th>
            <th>Map to agent</th>
          </tr>
        </thead>
        <tbody>
          {unmapped.map((u) => (
            <UnmappedRow
              key={u.writing_id}
              u={u}
              existingAgents={existingAgents}
              sys={sys}
              onSave={onSave}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UnmappedRow({
  u,
  existingAgents,
  sys,
  onSave,
}: {
  u: UnmappedWritingId;
  existingAgents: Agent[];
  sys: System;
  onSave: (
    writingId: string,
    agentName: string,
    opts?: { direct_pay_by_carrier?: boolean; non_paid?: boolean },
  ) => Promise<void> | void;
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await Promise.resolve(onSave(u.writing_id, name.trim()));
    } finally {
      setSaving(false);
      setName('');
    }
  };

  return (
    <tr>
      <td className="font-mono text-xs">{u.writing_id}</td>
      <td className="text-right font-mono">{u.row_count}</td>
      <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
        {u.sample_policyholders.slice(0, 2).join(', ')}
        {u.sample_policyholders.length > 2 && '…'}
      </td>
      <td>
        <div className="flex items-center gap-2">
          <AgentCombobox
            value={name}
            onChange={setName}
            existingAgents={existingAgents}
            sys={sys}
            onEnter={submit}
          />
          <button
            onClick={submit}
            disabled={!name.trim() || saving}
            className="btn btn-primary py-1 px-2 text-xs flex items-center gap-1 disabled:opacity-40"
          >
            <Plus size={12} />
            Map
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Combobox: type to filter existing agents, click one OR type a new name.
 * The dropdown shows existing matches plus a "+ Create new agent: …" option
 * when the typed value doesn't exactly match any existing.
 */
function AgentCombobox({
  value,
  onChange,
  existingAgents,
  sys,
  onEnter,
}: {
  value: string;
  onChange: (v: string) => void;
  existingAgents: Agent[];
  sys: System;
  onEnter?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Recompute dropdown position whenever it opens or page scrolls/resizes
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) {
        setPos({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 220),
        });
      }
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Click-outside both wrapper AND the (portaled) dropdown
  useEffect(() => {
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapperRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const trimmed = value.trim().toLowerCase();
  const matches = trimmed
    ? existingAgents.filter((a) => a.display_name.toLowerCase().includes(trimmed))
    : existingAgents;
  const exact = existingAgents.find((a) => a.display_name.toLowerCase() === trimmed);
  const showCreate = trimmed && !exact;

  return (
    <div ref={wrapperRef} className="relative" style={{ minWidth: '220px' }}>
      <div className="flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setOpen(false);
              onEnter?.();
            }
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder="Pick or create agent"
          className="input text-xs"
          style={{ paddingRight: '24px', width: '100%' }}
        />
        <ChevronDown
          size={12}
          className="absolute right-2 pointer-events-none"
          style={{ color: 'var(--color-text-muted)' }}
        />
      </div>
      {open &&
        pos &&
        (matches.length > 0 || showCreate) &&
        createPortal(
          <div
            ref={dropdownRef}
            className="dropdown-menu max-h-64 overflow-y-auto"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 10000,
            }}
          >
            {matches.slice(0, 30).map((a) => (
              <button
                key={a.agent_id}
                type="button"
                onClick={() => {
                  onChange(a.display_name);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-1.5 text-xs flex items-center justify-between"
                style={{ color: 'var(--color-text)' }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = 'var(--color-card-hover)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = 'transparent')
                }
              >
                <span>{a.display_name}</span>
                {isHouseForSystem(a, sys) && (
                  <span className="badge badge-gray text-xs">house</span>
                )}
              </button>
            ))}
            {showCreate && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onEnter?.();
                }}
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-1"
                style={{
                  color: 'var(--color-primary)',
                  borderTop:
                    matches.length > 0 ? '1px solid var(--color-border)' : 'none',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = 'var(--color-card-hover)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = 'transparent')
                }
              >
                <Plus size={11} />
                Create new agent: <strong>{value.trim()}</strong>
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

function ExistingMappingsSection({
  mappings,
  existingAgents,
  sys,
  onSave,
  onDelete,
}: {
  mappings: Mapping[];
  existingAgents: Agent[];
  sys: System;
  onSave: (
    writingId: string,
    agentName: string,
    opts?: { direct_pay_by_carrier?: boolean; non_paid?: boolean },
  ) => Promise<void> | void;
  onDelete: (writingId: string) => void;
}) {
  return (
    <div className="card p-0 overflow-hidden">
      <div
        className="px-4 py-3"
        style={{ borderBottom: '1px solid var(--color-border)' }}
      >
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          Existing mappings ({mappings.length})
        </h3>
      </div>
      {mappings.length === 0 ? (
        <p className="p-6 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
          None yet.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Writing ID</th>
              <th>Agent</th>
              <th className="text-right">Rows</th>
              <th>Source</th>
              <th title="Fallback only — column P now determines direct-pay. This flag applies only when the writer column is blank (and on legacy master-xlsx imports).">
                Direct pay <span style={{ color: 'var(--color-text-muted)', cursor: 'help' }}>ⓘ</span>
              </th>
              <th title="When checked, NEW imports for this writing ID are auto-marked paid and excluded from our pay flow. Use for your own NPN, agency-owned IDs, or anyone you never pay out. Existing rows are unaffected.">
                Non-paid
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((m) => (
              <ExistingRow
                key={m.writing_id}
                m={m}
                existingAgents={existingAgents}
                sys={sys}
                onSave={onSave}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ExistingRow({
  m,
  existingAgents,
  sys,
  onSave,
  onDelete,
}: {
  m: Mapping;
  existingAgents: Agent[];
  sys: System;
  onSave: (
    writingId: string,
    agentName: string,
    opts?: { direct_pay_by_carrier?: boolean; non_paid?: boolean },
  ) => Promise<void> | void;
  onDelete: (writingId: string) => void;
}) {
  const [name, setName] = useState(m.agent_name);
  // Optimistic overrides — flip immediately on click, reconcile when the
  // server response lands. `undefined` means "use server value".
  const [pendingDirectPay, setPendingDirectPay] = useState<boolean | undefined>(undefined);
  const [pendingNonPaid, setPendingNonPaid] = useState<boolean | undefined>(undefined);

  // When the server data updates (refetch lands), drop any stale optimistic
  // value if it now matches the server.
  useEffect(() => {
    if (pendingDirectPay !== undefined && pendingDirectPay === (m.direct_pay_by_carrier === true)) {
      setPendingDirectPay(undefined);
    }
    if (pendingNonPaid !== undefined && pendingNonPaid === (m.non_paid === true)) {
      setPendingNonPaid(undefined);
    }
  }, [m.direct_pay_by_carrier, m.non_paid, pendingDirectPay, pendingNonPaid]);

  const directPay = pendingDirectPay !== undefined ? pendingDirectPay : m.direct_pay_by_carrier === true;
  const nonPaid = pendingNonPaid !== undefined ? pendingNonPaid : m.non_paid === true;
  const dirty = name.trim() !== m.agent_name;

  const toggleDirectPay = async () => {
    const next = !directPay;
    setPendingDirectPay(next);
    try {
      await onSave(m.writing_id, m.agent_name, { direct_pay_by_carrier: next });
    } catch {
      // Roll back on failure — refetch will overwrite us with truth shortly
      setPendingDirectPay(!next);
    }
  };
  const toggleNonPaid = async () => {
    const next = !nonPaid;
    setPendingNonPaid(next);
    try {
      await onSave(m.writing_id, m.agent_name, { non_paid: next });
    } catch {
      setPendingNonPaid(!next);
    }
  };

  return (
    <tr>
      <td className="font-mono text-xs">{m.writing_id}</td>
      <td>
        <AgentCombobox
          value={name}
          onChange={setName}
          existingAgents={existingAgents}
          sys={sys}
          onEnter={() => {
            if (dirty) onSave(m.writing_id, name.trim());
          }}
        />
      </td>
      <td className="text-right font-mono text-sm">{m.row_count}</td>
      <td>
        <span className="badge badge-gray text-xs">{m.source}</span>
      </td>
      <td>
        <label
          className="flex items-center gap-2 text-xs cursor-pointer"
          style={{ color: 'var(--color-text)' }}
        >
          <input
            type="checkbox"
            checked={directPay}
            onChange={toggleDirectPay}
          />
          {directPay ? (
            <span className="badge badge-warning text-xs">direct-pay</span>
          ) : (
            <span style={{ color: 'var(--color-text-muted)' }}>off</span>
          )}
          <span
            className="cursor-help"
            style={{ color: 'var(--color-text-muted)' }}
            title="Fallback only — column P now determines direct-pay. This flag applies only when the writer column is blank (and on legacy master-xlsx imports)."
          >
            ⓘ
          </span>
        </label>
      </td>
      <td>
        <label
          className="flex items-center gap-2 text-xs cursor-pointer"
          style={{ color: 'var(--color-text)' }}
          title="Future imports for this writing ID will be auto-marked paid and excluded from our pay flow. Use for your own NPN, agency-owned IDs, or anyone you never pay out."
        >
          <input type="checkbox" checked={nonPaid} onChange={toggleNonPaid} />
          {nonPaid ? (
            <span className="badge badge-gray text-xs">non-paid</span>
          ) : (
            <span style={{ color: 'var(--color-text-muted)' }}>off</span>
          )}
        </label>
      </td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-1">
          {dirty && (
            <button
              onClick={() => onSave(m.writing_id, name.trim())}
              className="btn btn-primary py-1 px-2 text-xs flex items-center gap-1"
            >
              <Save size={12} />
              Save
            </button>
          )}
          <button
            onClick={() => onDelete(m.writing_id)}
            className="btn-icon"
            style={{ color: 'var(--color-text-muted)' }}
            aria-label="Delete mapping"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}
